// Simple utility to strip basic RTF control words / braces and return plain text.

// CP1252 (Windows-1252) overrides for the 0x80–0x9F range, where the byte values
// diverge from Latin-1 (smart quotes, dashes, ellipsis, etc.). Outside this range
// the byte value equals the Unicode codepoint — ASCII below 0x80, Latin-1 at/above
// 0xA0 — so String.fromCharCode handles those directly.
const CP1252_OVERRIDES: Record<number, string> = {
  0x80: '€', // €
  0x82: '‚', // ‚
  0x83: 'ƒ', // ƒ
  0x84: '„', // „
  0x85: '…', // …
  0x86: '†', // †
  0x87: '‡', // ‡
  0x88: 'ˆ', // ˆ
  0x89: '‰', // ‰
  0x8a: 'Š', // Š
  0x8b: '‹', // ‹
  0x8c: 'Œ', // Œ
  0x8e: 'Ž', // Ž
  0x91: '‘', // ‘
  0x92: '’', // ’
  0x93: '“', // “
  0x94: '”', // ”
  0x95: '•', // •
  0x96: '–', // –
  0x97: '—', // —
  0x98: '˜', // ˜
  0x99: '™', // ™
  0x9a: 'š', // š
  0x9b: '›', // ›
  0x9c: 'œ', // œ
  0x9e: 'ž', // ž
  0x9f: 'Ÿ', // Ÿ
};

const decodeRtfHex = (hex: string): string => {
  const code = parseInt(hex, 16);
  return CP1252_OVERRIDES[code] ?? String.fromCharCode(code);
};

const rtfToText = (rtf: string): string => {
  if (!rtf) return '';
  let text = rtf
    // Remove font table declarations FIRST, while the \fonttbl keyword is still
    // intact (the control-word strip below would otherwise erase the keyword and
    // leak the font names, e.g. "Arial;", into the output). Handles both flat
    // ({\fonttbl\f0 Arial;}) and nested ({\fonttbl{\f0 Arial;}{\f1 Times;}}) forms.
    // (replaced with a space, not '', so the removed group still delimits any
    // control word that preceded it — e.g. the \rtf1 in "{\rtf1{\fonttbl...}text}")
    .replace(/\{\\fonttbl[^{}]*(?:\{[^{}]*\}[^{}]*)*\}/gi, ' ')
    // Replace paragraph tags with newlines. The \b stops this from prefix-matching
    // longer control words like Word's \pararsid1234 or \pardirnatural (those fall
    // through to the generic control-word strip instead).
    .replace(/\\par[d]?\b/gi, '\n')
    .replace(/\\[a-zA-Z]+-?\d*\b/g, '') // control words with optional numeric arg
    .replace(/[{}]/g, '') // braces
    // Decode escaped hex chars (\'xx) AFTER the strips above: decoding first would
    // inject characters the strips then mangle (\'5c -> "\" would make the
    // control-word strip eat the following word; \'7b/\'7d -> literal braces
    // would be deleted by the brace strip).
    .replace(/\\'([0-9a-fA-F]{2})/g, (_match, hex) => decodeRtfHex(hex))
    .replace(/\n[\s\n]*/g, '\n') // collapse multiple newlines & whitespace
    .trim();
  return text;
};

export default rtfToText;
